import React, { useState, useEffect, useCallback } from 'react';
import P, { InferProps } from 'prop-types';
import TextField from '@mui/material/TextField';
import { FIELD_TYPES } from '../../../types';

const convertStringValueToNumber = (
  fieldType: string,
  newValue: number | string | null | undefined
) => {
  let parsedValue = newValue;

  if (fieldType === FIELD_TYPES.NUMBER && typeof newValue === 'string') {
    // Float
    if (newValue && (newValue.includes('.') || newValue.includes(','))) {
      parsedValue = parseFloat(newValue);
      // Integer
    } else {
      parsedValue = newValue ? parseInt(newValue) : null;
    }
  }

  return parsedValue;
};

function TextInput({
  setChange,
  setValidation,
  field,
  isReadOnly,
  label,
  helperText,
  hideValidationError,
  fullWidth = false,
  numRows = 1,
  maxLength = undefined,
}: InferProps<typeof TextInput.propTypes>) {
  const [value, setValue] = useState(field.value);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isDirty, setIsdirty] = useState(false);

  useEffect(() => {
    setValue(field.value);
  }, [field]);

  useEffect(() => {
    const validate = async (newValue: string | number | null | undefined) => {
      const yupSchema = field.validation;
      const parsedValue = convertStringValueToNumber(field.type, newValue);

      try {
        await yupSchema.validate(parsedValue);
        setErrorMessage('');
      } catch (error: any) {
        if (isDirty || !hideValidationError) {
          setErrorMessage(error.message);
        }
      } finally {
        setValidation(field.key, parsedValue, field.validation);
      }
    };
    if (field.validation) {
      validate(value);
    }
  }, [field, hideValidationError, isDirty, setValidation, value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsedValue = convertStringValueToNumber(field.type, e.target.value);
      setValue(parsedValue);
      setChange(field.key, parsedValue, field.validation, field.isTeamDataValue);
      setIsdirty(true);
    },
    [field, setChange]
  );

  return (
    <TextField
      disabled={field.key === 'Not available' || !!isReadOnly}
      value={value || ''}
      type={field.type}
      onChange={handleChange}
      id={field.id}
      label={label ?? field.title}
      helperText={errorMessage || helperText || field.helperText}
      error={!!errorMessage}
      variant="outlined"
      fullWidth={fullWidth ?? false}
      multiline={numRows > 1}
      minRows={numRows}
      maxRows={numRows}
      inputProps={{ maxLength: maxLength }}
    />
  );
}

TextInput.propTypes = {
  field: P.shape({
    title: P.string.isRequired,
    id: P.string.isRequired,
    type: P.string.isRequired,
    validation: P.any,
    key: P.string,
    value: P.oneOfType([P.string, P.number]),
    isTeamDataValue: P.bool,
    helperText: P.string,
  }).isRequired,
  setChange: P.func.isRequired,
  setValidation: P.func.isRequired,
  hideValidationError: P.bool,
  isReadOnly: P.bool,
  fullWidth: P.bool,
  numRows: P.number.isRequired,
  label: P.string,
  helperText: P.string,
  maxLength: P.number,
};

TextInput.defaultProps = {
  numRows: 1,
};

export default React.memo(TextInput);
